import { useCallback, useRef } from 'react';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { FTU_INSURANCE_EXPERIMENT_EXPOSURE } from '../../../../../../constants/analytics-events';
import { RateShopperAutomationRuleProvider } from '../../../../../../contexts/rate-shopper-automation-rule-context';
import { SystemAutomationContextProvider } from '../../../../../../contexts/system-automation-context';
import { useAreFeatureFlagsReady } from '../../../../../../feature-flags/hooks/use-are-feature-flags-ready';
import { useEnableDynamicPricing } from '../../../../../../feature-flags/hooks/use-enable-dynamic-pricing';
import { useEnableInsurancePromoExtended } from '../../../../../../feature-flags/hooks/use-enable-insurance-promo-extended';
import { useShowInsurancePromoExtendedUi } from '../../../../../../feature-flags/hooks/use-show-insurance-promo-extended-ui';
import { useIsCanadian } from '../../../../../../hooks/use-is-canadian';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { RateShopperContextProvider } from '../../../../../settings/rate-shopper/context/rate-shopper-context';
import { useFilteredTodoCards } from '../../../todo-cards/helpers/should-show-todo-card';
import { todoCardData } from '../../../todo-cards/todo-card-data';
import { todoPageIds } from '../../../todo-cards/todo-card-ids';
import { TodoCardsContainer } from '../../../todo-cards/todo-cards-container';
import { FTU_WIZARD_PAGE } from '../../constants';
import { InsurancePreferenceSelectionList } from './components/insurance-preference-selection-list/insurance-preference-selection-list';
import messages from './messages';
import { RateShopperSelectionList } from './rate-shopper-selection-list';
import style from './style.module.less';

export const DiscountedRatesStep = ({ onInsuranceSelectionChange }) => {
  const isCanadian = useIsCanadian();
  const hasDynamicPricing = useEnableDynamicPricing();
  const areFeatureFlagsReady = useAreFeatureFlagsReady();
  // Retained only for the experiment-exposure analytics event below. UI gating uses showInsurancePromoExtendedUi.
  const insurancePromoExtendedVariant = useEnableInsurancePromoExtended();
  const showInsurancePromoExtendedUi = useShowInsurancePromoExtendedUi();
  // Latch true once the gate flips on — prevents unmount flicker if LD re-evaluation flips it back off mid-session.
  const wasInsurancePromoExtendedUiShownRef = useRef(showInsurancePromoExtendedUi);
  if (showInsurancePromoExtendedUi) wasInsurancePromoExtendedUiShownRef.current = true;
  const shouldShowInsurancePreferences = wasInsurancePromoExtendedUiShownRef.current;
  const todoCards = useFilteredTodoCards(todoCardData, {
    pageWhereRendered: todoPageIds.DISCOUNTED_RATES_STEP,
  });

  const hasTrackedExposureRef = useRef(false);
  if (areFeatureFlagsReady && !hasTrackedExposureRef.current) {
    hasTrackedExposureRef.current = true;
    Segment.trackEvent(FTU_INSURANCE_EXPERIMENT_EXPOSURE, {
      variant: insurancePromoExtendedVariant,
      page: FTU_WIZARD_PAGE,
    });
  }

  const stepTitle = shouldShowInsurancePreferences ? messages.stepTitleWithInsurance : messages.stepTitle;

  let descriptionMessage = messages.stepDescription;
  if (shouldShowInsurancePreferences) {
    descriptionMessage = messages.stepDescriptionWithInsurance;
  } else if (hasDynamicPricing) {
    descriptionMessage = messages.stepDescriptionRateShopper;
  }

  const descriptionText = <Text textType={TextType.Description} value={descriptionMessage} />;

  const redirectToAutomationSettings = useCallback(() => {
    // TODO: Replace with the actual KB link URL if available in messages.shippingCreditsKBLink SPD-23261
    return null;
  }, []);

  return (
    <FlexColumn className={style['discounted-rates-step']}>
      <FlexColumn className={style['discount-rates-step-header']}>
        <Text className={style.header} textType={TextType.H2} value={stepTitle} />
        {descriptionText}
        <Button className={style.link} onClick={redirectToAutomationSettings} theme={THEME_CONSTANTS.link}>
          {hasDynamicPricing && (
            <FlexRow>
              <FormattedMessage {...messages.shippingCreditsKBLink} />
              <FontAwesomeIcon className={style['link-icon']} icon={faArrowUpRightFromSquare} />
            </FlexRow>
          )}
        </Button>
      </FlexColumn>
      <SystemAutomationContextProvider>
        {!isCanadian && (
          <RateShopperContextProvider>
            <RateShopperAutomationRuleProvider>
              <RateShopperSelectionList />
            </RateShopperAutomationRuleProvider>
          </RateShopperContextProvider>
        )}
        {shouldShowInsurancePreferences && <InsurancePreferenceSelectionList onSelectionChange={onInsuranceSelectionChange} />}
      </SystemAutomationContextProvider>
      <TodoCardsContainer todoCards={todoCards} />
    </FlexColumn>
  );
};
